/**
 * 活动选择器组件
 *
 * @description
 * 用于选择活动的下拉选择器
 * 支持：
 * - 受控组件模式
 * - 加载状态显示
 * - 自定义样式
 *
 * @example
 * ```tsx
 * <ActivitySelect
 *   value={activityId}
 *   onChange={(value) => setActivityId(value)}
 *   placeholder="选择活动"
 * />
 * ```
 */

import { getAvailableActivities, IActivityItem } from "@/services/gaoding";
import { useMemoizedFn } from "ahooks";
import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import HBSelect from "../HBInput/select";

interface ActivitySelectProps {
  /** 当前选中的活动 ID */
  value?: string;
  /** 值变化回调 */
  onChange?: (value: string) => void;
  /** 占位符文本 */
  placeholder?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 自定义类名 */
  className?: string;
  /** React ref */
  ref?: React.Ref<any>;
}

/**
 * 活动选择器组件
 */
export default function ActivitySelect({
  value,
  onChange,
  placeholder = "选择活动",
  disabled = false,
  className,
  ref,
}: ActivitySelectProps) {
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState<IActivityItem[]>([]);

  /**
   * 判断活动是否活跃
   * 根据开始时间和结束时间判断活动是否在有效期内
   */
  const isActivityActive = useMemoizedFn((activity: IActivityItem): boolean => {
    if (!activity.start_time && !activity.end_time) {
      // 如果没有时间限制，默认认为活动是活跃的
      return true;
    }

    const now = new Date().getTime();
    const startTime = activity.start_time
      ? new Date(activity.start_time).getTime()
      : 0;
    const endTime = activity.end_time
      ? new Date(activity.end_time).getTime()
      : Infinity;

    return now >= startTime && now <= endTime;
  });

  /**
   * 获取活动列表
   */
  const fetchActivities = useMemoizedFn(async () => {
    setLoading(true);
    try {
      const result = await getAvailableActivities();
      setActivities(result || []);
    } catch (error) {
      console.error("获取活动列表失败:", error);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  });

  /**
   * 组件挂载时获取活动列表
   */
  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  /**
   * 将活动数据转换为 Select 组件的 options 格式
   */
  const options = useMemo(() => {
    return activities
      .filter((activity) => {
        // 过滤掉没有 id 或 name 的活动
        if (!activity.id || !activity.name) {
          return false;
        }
        // 只显示活跃的活动
        return isActivityActive(activity);
      })
      .map((activity) => ({
        label: activity.name!,
        value: activity.id,
      }));
  }, [activities, isActivityActive]);

  /**
   * 选择变化处理
   */
  const handleChange = useMemoizedFn((selectedValue: string) => {
    onChange?.(selectedValue);
  });

  return (
    <HBSelect
      ref={ref}
      className={classNames(className)}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      loading={loading}
      options={options}
      allowClear
      optionFilterProp="label"
      noWrapper
    />
  );
}
